import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, type Icon } from '@phosphor-icons/react';
import { useDialogFokus } from './fokus';

/**
 * Hülle für Formular-Dialoge (Glas stark, Radius 26): Kopf mit Icon, Titel und Schließen, scrollender Inhalt, Fuß.
 * Escape und Klick auf den Hintergrund schließen – nicht, solange etwas läuft.
 * vollbild: mobil über den ganzen Bildschirm (Handoff Zähler §5.6); iconFarbe: z. B. Typfarbe statt Akzent.
 */
export function DialogHuelle({ titel, untertitel, icon: IconKomp, onSchliessen, laeuft = false, breite = 460, children, fuss, vollbild = false, iconFarbe }: {
  titel: string; untertitel?: string; icon: Icon; onSchliessen: () => void; laeuft?: boolean; breite?: number;
  children: React.ReactNode; fuss?: React.ReactNode; vollbild?: boolean; iconFarbe?: { farbe: string; flaeche: string };
}) {
  const dialog = useDialogFokus<HTMLDivElement>();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !laeuft) onSchliessen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen, laeuft]);
  return createPortal(
    <div className={`fixed inset-0 flex items-center justify-center ${vollbild ? '' : 'p-4'}`} style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={e => { if (e.target === e.currentTarget && !laeuft) onSchliessen(); }}>
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={titel} className="immo-glas-stark outline-none immo-side-in flex flex-col w-full"
        style={vollbild
          ? { maxWidth: 'none', height: '100dvh', maxHeight: '100dvh', borderRadius: 0, padding: '16px 16px max(16px, env(safe-area-inset-bottom))', gap: 14, color: 'var(--ink)', background: 'var(--bg)' }
          : { maxWidth: breite, maxHeight: 'calc(100dvh - 32px)', borderRadius: 26, padding: 20, gap: 14, color: 'var(--ink)' }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: iconFarbe?.flaeche ?? 'var(--acc-soft)' }}>
            <IconKomp weight="fill" size={19} color={iconFarbe?.farbe ?? 'var(--acc)'} />
          </span>
          <span className="flex flex-col flex-1 min-w-0">
            <span style={{ fontSize: 18, fontWeight: 800 }}>{titel}</span>
            {untertitel && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }} title={untertitel}>{untertitel}</span>}
          </span>
          <button type="button" aria-label="Schließen" data-tip="Schließen" onClick={onSchliessen} disabled={laeuft} className="flex items-center justify-center cursor-pointer shrink-0"
            style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
            <X weight="bold" size={13} />
          </button>
        </div>
        <div className="immo-scroll flex flex-col min-h-0 overflow-y-auto" style={{ gap: 12, flex: vollbild ? '1 1 auto' : undefined }}>{children}</div>
        {fuss && <div className="flex flex-wrap" style={{ gap: 10 }}>{fuss}</div>}
      </div>
    </div>,
    document.body,
  );
}
